import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet-async";
import SetInterval from "set-interval";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import DateAdapter from "@mui/lab/AdapterDateFns";
import DatePicker from "@mui/lab/DatePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { enGB } from "date-fns/locale";
import moment from "moment";

import { BACKEND_URL, siteConstants } from "../../../constants";
import LinearProgressWithLabel from "../../LinearProgressWithLabel";
import { alertActions, csActions } from "../../../redux/actions";
import { csServices } from "../../../services";
import PdfIcon from "../../../assets/images/pdf.png";
import DocIcon from "../../../assets/images/doc.png";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const getDropzoneBackgroundColor = (props) => {
  if (props.isDragActive)
    return "#fafafa";

  return "#ebebeb";
}

const DropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  border-width: 1px;
  border-radius: 4px;
  border-style: dashed;
  border-color: #aaaaaa;
  background-color: ${props => getDropzoneBackgroundColor(props)};
  color: #8a8a8a;
  outline: none;
`;

const StyledDropzone = (props) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: "application/pdf",
    maxFiles: 1,
    multiple: false,
  });

  useEffect(() => {
    if (acceptedFiles.length > 0)
      props.onFileAccepted(acceptedFiles[0]);
  }, [acceptedFiles]);

  useEffect(() => {
    if (fileRejections.length > 0)
      props.onFileRejected();
  }, [fileRejections]);

  return (
    <>
      <DropzoneContainer {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
        <input {...getInputProps()} />
        <Typography align="center" variant="subtitle2">
          <p>Drag 'n' drop CS report file here, or click to select file</p>
        </Typography>
        <CloudUploadIcon />
      </DropzoneContainer>
      {acceptedFiles.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {acceptedFiles[0].name} ({(acceptedFiles[0].size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
      {fileRejections.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {fileRejections[0].file.name} ({(fileRejections[0].file.size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
    </>
  )
}

const CsReportsPage = (props) => {
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - siteConstants.CS_REPORT_LIST_DAYS);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(new Date());
  const [reports, setReports] = useState([]);
  const [reportFile, setReportFile] = useState(null);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 120,
      renderCell: (params: GridRenderCellParams<string>) => {
        return (
          <Link
            href={`/assessment-report/?id=${params.value}`}
            target="_blank"
            rel="noopener"
          >
            {params.value.slice(-8)}
          </Link>
        );
      }
    },
    {
      field: "csReport",
      headerName: "CS Report",
      width: 120,
      align: "center",
      renderCell: (params: GridRenderCellParams<string>) => {
        return (
          <Link
            href={`${BACKEND_URL}${params.value}`}
            target="_blank"
            rel="noopener"
          >
            <img src={PdfIcon} style={{ width: "24px", marginTop: "12px" }} />
          </Link>
        );
      },
    },
    { field: "clientName", headerName: "Client Name", flex: 1, },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (params: GridRenderCellParams<object>) => {
        const report = params.value;
        if (report.status === "processing") {
          return (
            <Box sx={{ width: "100%" }}>
              <LinearProgressWithLabel value={report.process_progress} />
            </Box>
          );
        } else if (
          (report.status === "ready for review")
          || (report.status === "assessment generated")
        ) {
          return (
            <Box sx={{ width: "100%", color: "green" }}>
              {report.status}
            </Box>
          );
        } else if (report.status === "processing failed") {
          return (
            <Box sx={{ width: "100%", color: "red" }}>
              {report.status}
            </Box>
          );
        } else {
          return report.status;
        }
      },
    },
    {
      field: "assessmentReportGallupA5Standee",
      headerName: "A5",
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value)
          return (
            <Link
              href={`${BACKEND_URL}${params.value}`}
              target="_blank"
              rel="noopener"
            >
              <img src={DocIcon} style={{ width: "24px", marginTop: "12px" }} />
            </Link>
          );
        else
          return "";
      },
    },
    {
      field: "assessmentReportWithoutCsText",
      headerName: "Without CS Text",
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value)
          return (
            <Link
              href={`${BACKEND_URL}${params.value}`}
              target="_blank"
              rel="noopener"
            >
              <img src={DocIcon} style={{ width: "24px", marginTop: "12px" }} />
            </Link>
          );
        else
          return "";
      },
    },
    {
      field: "assessmentReportMediaTraining",
      headerName: "Media Training",
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value)
          return (
            <Link
              href={`${BACKEND_URL}${params.value}`}
              target="_blank"
              rel="noopener"
            >
              <img src={DocIcon} style={{ width: "24px", marginTop: "12px" }} />
            </Link>
          );
        else
          return "";
      },
    },
    {
      field: "assessmentReportTvPresenter",
      headerName: "TV Presenter",
      width: 150,
      align: "center",
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value)
          return (
            <Link
              href={`${BACKEND_URL}${params.value}`}
              target="_blank"
              rel="noopener"
            >
              <img src={DocIcon} style={{ width: "24px", marginTop: "12px" }} />
            </Link>
          );
        else
          return "";
      },
    },
    {
      field: "assessmentReportKYI",
      headerName: "KYI",
      width: 60,
      align: "center",
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value)
          return (
            <Link
              href={`${BACKEND_URL}${params.value}`}
              target="_blank"
              rel="noopener"
            >
              <img src={DocIcon} style={{ width: "24px", marginTop: "12px" }} />
            </Link>
          );
        else
          return "";
      },
    },
    {
      field: "assessmentReportHRC",
      headerName: "HRC",
      width: 60,
      align: "center",
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value)
          return (
            <Link
              href={`${BACKEND_URL}${params.value}`}
              target="_blank"
              rel="noopener"
            >
              <img src={DocIcon} style={{ width: "24px", marginTop: "12px" }} />
            </Link>
          );
        else
          return "";
      },
    },
    { field: "timestamp", headerName: "Timestamp", width: 160, },
  ];

  const onFileAccepted = (file) => {
    props.alertReset();
    setReportFile(file);
  }

  const onFileRejected = () => {
    props.alertError(siteConstants.ERROR_MSG_INVALID_CS_REPORT_FILE);
  }

  const handleRefreshReports = () => {
    props.alertReset();
    props.listReports(startDate, endDate);
  }

  const getReportValidationStatus = () => {
    props.getReportValidationStatus(props.reportId);
  }

  const pollProcessingStatus = () => {
    csServices.listReports(startDate, endDate)
      .then(response => {
        const _reports = [];
        const statuses = new Set();
        if (response.data) {
          response.data.map((report) => {
            const timestamp = moment(report.created);
            _reports.push({
              id: report.id,
              csReport: report.file,
              clientName: report.client_name,
              status: report,
              assessmentReportGallupA5Standee: report.assessment_report_gallup_a5_standee,
              assessmentReportWithoutCsText: report.assessment_report_without_cs_text,
              assessmentReportMediaTraining: report.assessment_report_media_training,
              assessmentReportTvPresenter: report.assessment_report_tv_presenter,
              assessmentReportKYI: report.assessment_report_kyi,
              assessmentReportHRC: report.assessment_report_hrc,
              timestamp: timestamp.fromNow(),
            });
            statuses.add(report.status);
          });
        }
        setReports(_reports);
      })
      .catch(error => {
        if (error.response)
          props.alertError(error.response.data.detail || siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
        else
          props.alertError(siteConstants.ERROR_MSG_SERVER_COMMUNICATION);
      });
  }

  useEffect(() => {
    if (props.isValid)
      props.processReport(props.reportId);
  }, [props.isValid])

  useEffect(() => {
    if (props.isInvalid)
      props.alertError(siteConstants.ERROR_MSG_INVALID_CS_REPORT_FILE);
  }, [props.isInvalid])

  useEffect(() => {
    if (props.reportId && props.isValidating) {
      SetInterval.start(getReportValidationStatus, 1000, "pollValidationStatus");
    } else {
      SetInterval.clear("pollValidationStatus")
    }
  }, [props])

  useEffect(() => {
    if (reportFile)
      props.validateReport(reportFile);
  }, [reportFile]);

  useEffect(() => {
    const _reports = [];
    if (props.reports) {
      props.reports.map((report) => {
        const timestamp = moment(report.created);
        _reports.push({
          id: report.id,
          csReport: report.file,
          clientName: report.client_name,
          status: report,
          assessmentReportGallupA5Standee: report.assessment_report_gallup_a5_standee,
          assessmentReportWithoutCsText: report.assessment_report_without_cs_text,
          assessmentReportMediaTraining: report.assessment_report_media_training,
          assessmentReportTvPresenter: report.assessment_report_tv_presenter,
          assessmentReportKYI: report.assessment_report_kyi,
          assessmentReportHRC: report.assessment_report_hrc,
          timestamp: timestamp.fromNow(),
        })
      });
    }
    setReports(_reports);
  }, [props.reports])

  useEffect(() => {
    props.alertReset();
    props.listReports(startDate, endDate);
  }, [startDate])

  useEffect(() => {
    props.alertReset();
    props.listReports(startDate, endDate);
  }, [endDate])

  useEffect(() => {
    props.alertReset();
    SetInterval.clear("pollProcessingStatus");
    SetInterval.start(pollProcessingStatus, 2000, "pollProcessingStatus");
    return () => SetInterval.clear("pollProcessingStatus");
  }, [startDate, endDate])

  return (
    <>
      <Helmet>
        <title>CS Reports</title>
      </Helmet>

      <LocalizationProvider dateAdapter={DateAdapter} locale={enGB}>
        <Container
          maxWidth="xl"
          sx={{
            marginTop: "30px",
            textAlign: "left",
          }}
        >
          <Typography variant="h4">CS Reports</Typography>
          {
            props.alert.message &&
            <Alert variant="outlined" severity={ props.alert.type }>
              { props.alert.message }
            </Alert>
          }
          <StyledDropzone
            selectedFilePath={props.selectedFilePath}
            selectedFileSize={props.selectedFileSize}
            onFileAccepted={onFileAccepted}
            onFileRejected={onFileRejected}
          />
          {props.isValidating &&
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption">Validating</Typography>&nbsp;
              <CircularProgress color="primary" size={16} />
            </Box>
          }
          <Box
            sx={{
              display: "flex",
              gap: "20px",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              maxDate={new Date()}
              onChange={(newDate) => {
                setStartDate(newDate);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker
              label="End date"
              value={endDate}
              maxDate={new Date()}
              onChange={(newDate) => {
                setEndDate(newDate);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
            <IconButton
              size="large"
              onClick={handleRefreshReports}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          <DataGrid
            rows={reports}
            columns={columns}
            autoHeight={true}
            density="compact"
            hideFooterSelectedRowCount={true}
            loading={props.isListingReports}
          />
        </Container>
      </LocalizationProvider>
    </>
  );
}

const mapState = (state) => {
  const {
    isListingReports,
    reports,
    isValidating,
    reportId,
    isValid,
    isInvalid,
    isProcessing,
  } = state.cs;
  return {
    isListingReports,
    reports,
    isValidating,
    reportId,
    isValid,
    isInvalid,
    isProcessing,
    alert: state.alert,
  }
};

const actionCreators = {
  alertReset: alertActions.reset,
  alertError: alertActions.error,
  listReports: csActions.listReports,
  validateReport: csActions.validateReport,
  getReportValidationStatus: csActions.getReportValidationStatus,
  processReport: csActions.processReport,
};

export default connect(mapState, actionCreators)(CsReportsPage);
